import { useEffect, useState } from 'react';
import type { NewCustomerRequest, Staff } from '../../types';
import { formatPhoneNumber } from '../../../../../../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // 등록 성공 시 실행할 콜백 (예: 목록 새로고침)
}

type NewCustomerForm = Omit<NewCustomerRequest, 'budget' | 'totalContractAmount'> & {
  budget: string;
  totalContractAmount: string;
};

const getInitialForm = (): NewCustomerForm => ({
  groomName: '',
  groomPhone: '',
  brideName: '',
  bridePhone: '',
  contractType: '리허설+본식',
  rehearsalVenue: '',
  venueAddress: '',
  contractDt: new Date().toISOString().split('T')[0],
  assignedStaffIdx: 0,
  rehearsalDt: '',
  weddingDt: '',
  weddingVenue: '',
  status: '상담중',
  budget: '',
  totalContractAmount: '',
  referralSource: '인터넷 광고',
  referralSourceDetail: '',
  memo: ''
});

const parseNumberInput = (value: string) => {
  const cleaned = value.replace(/[^\d]/g, '');
  if (!cleaned) return 0;
  return Number(cleaned);
};

const normalizeDateTime = (value: string) => {
  if (!value) return value;
  if (value.length === 16) return `${value}:00`;
  return value;
};

const normalizeCustomerStatus = (status?: string) => {
  const normalized = (status || '').trim();
  if (normalized === '계약완료') return '계약';
  if (normalized === '완료') return '본식완료';
  if (normalized === '취소됨') return '취소';
  return normalized;
};

export default function NewCustomerModal({ isOpen, onClose, onSuccess }: Props) {
  const [newCustomer, setNewCustomer] = useState<NewCustomerForm>(getInitialForm());

  const [staffList, setStaffList] = useState<Staff[]>([]);

  // ✅ [추가] 모달 열릴 때 직원 목록 조회
  useEffect(() => {
    if (isOpen) {
      const fetchStaff = async () => {
        try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch('https://api.wedmin.biz/user?status=ACTIVE&size=100', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();
          // API 응답이 { items: [...] } 구조라고 가정 (GetUsers DTO 참고)
          console.log("result ",result)
          if (result?.data?.items) {
            setStaffList(result.data.items);
          }
        } catch (error) {
          console.error('직원 목록 조회 실패:', error);
        }
      };
      fetchStaff();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: NewCustomerRequest = {
        groomName: newCustomer.groomName,
        groomPhone: newCustomer.groomPhone,
        brideName: newCustomer.brideName,
        bridePhone: newCustomer.bridePhone,
        contractType: newCustomer.contractType,
        assignedStaffIdx: newCustomer.assignedStaffIdx,
        rehearsalVenue: newCustomer.rehearsalVenue,
        venueAddress: newCustomer.venueAddress,
        contractDt: newCustomer.contractDt,
        rehearsalDt: normalizeDateTime(newCustomer.rehearsalDt),
        weddingDt: normalizeDateTime(newCustomer.weddingDt),
        weddingVenue: newCustomer.weddingVenue,
        status: normalizeCustomerStatus(newCustomer.status),
        budget: parseNumberInput(newCustomer.budget),
        referralSource: newCustomer.referralSource,
        referralSourceDetail: newCustomer.referralSourceDetail,
        memo: newCustomer.memo,
        ...(newCustomer.totalContractAmount.trim()
          ? { totalContractAmount: parseNumberInput(newCustomer.totalContractAmount) }
          : {})
      };
      console.log("Creating new customer:", payload);
      
      // 실제 API 호출: POST /customer
      const token = localStorage.getItem('accessToken');
  
      const response = await fetch('https://api.wedmin.biz/customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorJson = await response.json().catch(() => null);
        throw new Error(errorJson?.message || 'Failed to create customer');
      }
      

      alert("신규 고객이 등록되었습니다.");
      if (onSuccess) onSuccess();
      onClose();
      
      // 폼 초기화
      setNewCustomer(getInitialForm());

    } catch (error) {
      console.error("Failed to create customer:", error);
      const message = error instanceof Error ? error.message : "고객 등록 중 오류가 발생했습니다.";
      alert(message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">신규 고객 등록</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 기본 정보 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">신랑명</label>
                <input
                  type="text"
                  value={newCustomer.groomName}
                  onChange={(e) => setNewCustomer({...newCustomer, groomName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">신랑 연락처</label>
                <input
                  type="tel"
                  value={newCustomer.groomPhone}
                  onChange={(e) => setNewCustomer({...newCustomer, groomPhone: formatPhoneNumber(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">신부명</label>
                <input
                  type="text"
                  value={newCustomer.brideName}
                  onChange={(e) => setNewCustomer({...newCustomer, brideName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">신부 연락처</label>
                <input
                  type="tel"
                  value={newCustomer.bridePhone}
                  onChange={(e) => setNewCustomer({...newCustomer, bridePhone: formatPhoneNumber(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 계약 정보 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
                <select 
                  value={newCustomer.assignedStaffIdx}
                  onChange={(e) => setNewCustomer({...newCustomer, assignedStaffIdx: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>선택해주세요</option>
                  {/* ✅ [수정] API 데이터 매핑 */}
                  {staffList.map((staff) => (
                    <option key={staff.userIdx} value={staff.userIdx}>
                      {staff.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">계약일</label>
                <input 
                  type="date" 
                  value={newCustomer.contractDt}
                  onChange={(e) => setNewCustomer({...newCustomer, contractDt: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">계약 유형</label>
                <select 
                  value={newCustomer.contractType}
                  onChange={(e) => setNewCustomer({...newCustomer, contractType: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>리허설+본식</option>
                  <option>본식만</option>
                  <option>리허설만</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                <select 
                  value={newCustomer.status}
                  onChange={(e) => setNewCustomer({...newCustomer, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>상담중</option>
                  <option>계약</option>
                  <option>진행중</option>
                  <option>본식완료</option>
                  <option>취소</option>
                </select>
              </div>
            </div>

            {/* 예식 정보 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">본식일</label>
                <input 
                  type="datetime-local" 
                  value={newCustomer.weddingDt}
                  onChange={(e) => setNewCustomer({...newCustomer, weddingDt: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">본식 장소</label>
                <input 
                  type="text" 
                  value={newCustomer.weddingVenue}
                  onChange={(e) => setNewCustomer({...newCustomer, weddingVenue: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">리허설일</label>
                <input 
                  type="datetime-local" 
                  value={newCustomer.rehearsalDt}
                  onChange={(e) => setNewCustomer({...newCustomer, rehearsalDt: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">리허설 장소</label>
                <input 
                  type="text" 
                  value={newCustomer.rehearsalVenue}
                  onChange={(e) => setNewCustomer({...newCustomer, rehearsalVenue: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 추가 정보 (API 필드 추가) */}
            <div className="grid grid-cols-2 gap-4">

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">유입 경로</label>
                <select 
                  value={newCustomer.referralSource}
                  onChange={(e) => setNewCustomer({...newCustomer, referralSource: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>인터넷 광고</option>
                  <option>지인소개</option>
                  <option>박람회</option>
                  <option>SNS</option>
                  <option>기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">유입 상세</label>
                <input 
                  type="text" 
                  value={newCustomer.referralSourceDetail}
                  onChange={(e) => setNewCustomer({...newCustomer, referralSourceDetail: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">예산</label>
                <input 
                  type="text"
                  inputMode="numeric"
                  value={newCustomer.budget}
                  onChange={(e) => setNewCustomer({...newCustomer, budget: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="예: 20,000,000  (숫자만 입력해주세요.)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">총 계약금액</label>
                <input 
                  type="text"
                  inputMode="numeric"
                  value={newCustomer.totalContractAmount}
                  onChange={(e) => setNewCustomer({...newCustomer, totalContractAmount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="예: 60,000,000  (숫자만 입력해주세요.)"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
              <textarea 
                value={newCustomer.memo}
                onChange={(e) => setNewCustomer({...newCustomer, memo: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                등록
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
